import { useSyncExternalStore } from "react";
import { ApiError, getSelectedContext, setSelectedContext } from "@/lib/api";
import { featureDisabledSignal, onFeatureDisabled } from "@/lib/feature-bus";

// ---------------------------------------------------------------------------
// Per-outlet feature switches (set by Dhaba support in the Ops portal).
//
// CRM: when switched off for an outlet, customer screens, campaigns,
// automations and feedback are hidden and every CRM API answers
// 403 FEATURE_DISABLED. Billing, basic customer capture, the loyalty points
// program and discounts stay on. The outlet's CRM data is kept.
//
// The flag rides on the selected context (outlets-staff -> outlet.crm_enabled)
// and is kept current by:
//   - GET /outlets/{id} and /auth/staff/me (whenever they are fetched),
//   - GET /outlets/{id}/features, polled every 5 minutes and on window focus,
//   - any API refusal with 403 FEATURE_DISABLED (flips it off at once).
// ---------------------------------------------------------------------------

export const CRM_FEATURE = "crm";

export const CRM_DISABLED_TITLE = "CRM is turned off for this outlet";
export const CRM_DISABLED_MESSAGE =
  "Customer screens, campaigns, automations and feedback are switched off by Dhaba support. Your data is kept. Contact Dhaba support to turn them back on.";

const CHANGE_EVENT = "dhaba:selected-outlet-features";

/** CRM is on unless an explicit `false` is stored (absent = on, the default). */
export function getSelectedCrmEnabled(): boolean {
  const outlet = getSelectedContext()?.outlet;
  return outlet?.crm_enabled !== false;
}

/** Store the flag on the selected context (only if `outletId`, when given, is the selected outlet). */
export function setSelectedCrmEnabled(enabled: boolean | null | undefined, outletId?: string): void {
  if (typeof enabled !== "boolean") return;
  const context = getSelectedContext();
  if (!context?.outlet) return;
  if (outletId && context.outlet.id !== outletId) return;
  if (context.outlet.crm_enabled === enabled) return;
  setSelectedContext({ ...context, outlet: { ...context.outlet, crm_enabled: enabled } });
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

/** CRM features are on for the selected outlet (true when unknown). */
export function useCrmEnabled(): boolean {
  return useSyncExternalStore(subscribe, getSelectedCrmEnabled, () => true);
}

/** The error is the server refusing a CRM call because CRM is off for the outlet. */
export function isCrmDisabledError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return featureDisabledSignal(error.status, error.data)?.feature === CRM_FEATURE;
}

// A CRM call refused with 403 FEATURE_DISABLED turns CRM off for that outlet at once.
onFeatureDisabled(({ feature, outletId }) => {
  if (feature === CRM_FEATURE) setSelectedCrmEnabled(false, outletId);
});
